import React, { useEffect, useState } from "react";
import { Link, MemoryRouter } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import ReCAPTCHA from "react-google-recaptcha";
import ContactFormFields from "../../components/Contact/ContactFormFields";
import OTPInput from "../../components/Input/OTPInput";
import { apiGet, apiPost } from "../../apiService";
import styles from "./ContactUsPage.module.scss";

// --- Centralized SVG Icons (Unified to Primary Color) ---
const Icons = {
  Check: () => (
    <svg
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
      width="1em"
      height="1em"
    >
      <polyline points="20 6 9 17 4 12"></polyline>
    </svg>
  ),
  ArrowRight: () => (
    <svg
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      width="1em"
      height="1em"
    >
      <line x1="5" y1="12" x2="19" y2="12"></line>
      <polyline points="12 5 19 12 12 19"></polyline>
    </svg>
  ),
  Shield: () => (
    <svg
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      width="1em"
      height="1em"
    >
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
    </svg>
  ),
  Zap: () => (
    <svg
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      width="1em"
      height="1em"
    >
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon>
    </svg>
  ),
  Mail: () => (
    <svg
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      width="1em"
      height="1em"
    >
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
      <polyline points="22,6 12,13 2,6"></polyline>
    </svg>
  ),
  Clock: () => (
    <svg
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      width="1em"
      height="1em"
    >
      <circle cx="12" cy="12" r="10"></circle>
      <polyline points="12 6 12 12 16 14"></polyline>
    </svg>
  ),
  Send: () => (
    <svg
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      width="1em"
      height="1em"
    >
      <line x1="22" y1="2" x2="11" y2="13"></line>
      <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
    </svg>
  ),
};

// --- Animations ---
const parentVariants = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.1 } },
};
const childVariants = {
  hidden: { opacity: 0, y: 20 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] },
  },
};

// --- Custom Contact Hero Art: Animated Connection Hub ---
const ContactHeroArt = () => (
  <div className={styles["contact-hero-art"]} aria-hidden>
    <div className={styles["contact-hero-art-glow"]} />
    <svg
      className={styles["contact-hero-svg"]}
      viewBox="0 0 440 380"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <filter id="hero-glow" x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="8" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        <linearGradient id="link-grad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#4f46e5" stopOpacity="0.6" />
          <stop offset="100%" stopColor="#0ea5e9" stopOpacity="0.2" />
        </linearGradient>
      </defs>

      {/* Decorative Grid */}
      <pattern
        id="grid-dots"
        width="30"
        height="30"
        patternUnits="userSpaceOnUse"
      >
        <circle cx="2" cy="2" r="1" fill="rgba(255,255,255,0.05)" />
      </pattern>
      <rect width="100%" height="100%" fill="url(#grid-dots)" />

      {/* Connection Streams */}
      <path
        d="M 60 190 Q 140 50 220 190 T 380 190"
        stroke="url(#link-grad)"
        strokeWidth="2"
        strokeDasharray="4 4"
        className="anim-dash"
      />
      <path
        d="M 80 140 Q 180 300 280 140"
        stroke="url(#link-grad)"
        strokeWidth="2"
        strokeDasharray="4 4"
        className="anim-dash"
        style={{ animationDirection: "reverse" }}
      />

      {/* Central Secure Node */}
      <g className="anim-pulse-box">
        <circle
          cx="220"
          cy="190"
          r="45"
          fill="rgba(15, 23, 42, 0.8)"
          stroke="#4f46e5"
          strokeWidth="2"
          filter="url(#hero-glow)"
        />
        <path
          d="M210 180 L230 180 L230 205 C230 215, 210 215, 210 205 Z"
          fill="#4f46e5"
          opacity="0.4"
        />
        <rect x="210" y="185" width="20" height="15" rx="2" fill="#a5b4fc" />
        <path
          d="M214 185 V178 A6 6 0 0 1 226 178 V185"
          stroke="#a5b4fc"
          strokeWidth="2.5"
          fill="none"
        />
      </g>

      {/* Incoming Messages/Data Nodes */}
      <g className="anim-float" style={{ animationDelay: "0.2s" }}>
        <circle
          cx="80"
          cy="140"
          r="12"
          fill="rgba(79, 70, 229, 0.2)"
          stroke="#4f46e5"
          strokeWidth="1.5"
        />
        <path d="M74 136 L86 136 L80 144 Z" fill="#4f46e5" />
      </g>
      <g className="anim-float" style={{ animationDelay: "0.6s" }}>
        <circle
          cx="360"
          cy="140"
          r="10"
          fill="rgba(14, 165, 233, 0.2)"
          stroke="#0ea5e9"
          strokeWidth="1.5"
        />
        <rect x="355" y="137" width="10" height="6" fill="#0ea5e9" />
      </g>
      <g className="anim-float" style={{ animationDelay: "0.9s" }}>
        <circle
          cx="220"
          cy="300"
          r="14"
          fill="rgba(16, 185, 129, 0.2)"
          stroke="#10b981"
          strokeWidth="1.5"
        />
        <path
          d="M215 300 L220 305 L225 295"
          stroke="#10b981"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </g>
    </svg>
  </div>
);

const REASON_OPTIONS = [
  "API Access & Pricing",
  "Enterprise SaaS / PaaS",
  "Security & Compliance Review",
  "General Support",
  "Other",
];

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// --- Google Ads conversion tracking ---
// gtag is loaded globally via gtag.js in public/index.html. Guard the call so
// a missing/blocked gtag (ad-blockers) never breaks form submission.
function gtag_report_conversion(url) {
  const callback = function () {
    if (typeof url !== "undefined") {
      window.location = url;
    }
  };
  if (typeof window.gtag === "function") {
    window.gtag("event", "conversion", {
      send_to: "AW-18122887112/yq32CMTksb8cEMif1cFD",
      event_callback: callback,
    });
  }
  return false;
}

const ContactUsPage = () => {
  const [formSent, setFormSent] = useState(false);
  const [captchaVerified, setCaptchaVerified] = useState(false);

  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");
  const [reason, setReason] = useState(REASON_OPTIONS[0]);
  const [message, setMessage] = useState("");

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Email OTP verification
  const [otpRequired, setOtpRequired] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [otpInfo, setOtpInfo] = useState("");

  // Check whether the contact form requires email OTP verification.
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await apiGet("catalog", "/public/contact-us/meta");
        if (active && res?.status === "success") {
          setOtpRequired(Boolean(res.data?.otpRequired));
        }
      } catch (err) {
        console.error("Contact meta fetch error:", err);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const validate = () => {
    const next = {};
    if (!fullName.trim()) next.fullName = "Full Name is required";
    if (!email.trim()) {
      next.email = "Email is required";
    } else if (!EMAIL_REGEX.test(email.trim())) {
      next.email = "Enter a valid email address";
    }
    if (!mobile.trim()) {
      next.mobile = "Mobile number is required";
    } else if (!/^\d{10,15}$/.test(mobile.trim())) {
      next.mobile = "Enter a valid mobile number";
    }
    if (!message.trim()) next.message = "Message is required";
    if (!reason) next.reason = "Please select a topic";
    if (otpRequired) {
      if (!otpSent) {
        next.otp = "Please verify your email first";
      } else if (!otp.trim()) {
        next.otp = "Verification code is required";
      }
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  // Sends the email verification code (only used when otpRequired).
  const handleVerifyEmail = async () => {
    if (sendingOtp) return;

    // Name + email must be valid before requesting a code.
    const next = {};
    if (!fullName.trim()) next.fullName = "Full Name is required";
    if (!email.trim()) {
      next.email = "Email is required";
    } else if (!EMAIL_REGEX.test(email.trim())) {
      next.email = "Enter a valid email address";
    }
    if (Object.keys(next).length > 0) {
      setErrors((prev) => ({ ...prev, ...next }));
      return;
    }

    setSendingOtp(true);
    setOtpInfo("");
    setErrorMessage("");

    try {
      const res = await apiPost("catalog", "/public/feedback/request-otp", {
        fullName: fullName.trim(),
        email: email.trim(),
      });

      if (res?.status === "success") {
        setOtpSent(true);
        setOtpInfo(res?.message || "Verification code sent");
      } else {
        setErrorMessage(
          res?.message || "Could not send verification code. Please try again.",
        );
      }
    } catch (err) {
      console.error("Request OTP error:", err);
      setErrorMessage(
        err?.response?.data?.message ||
          "Could not send verification code. Please try again.",
      );
    } finally {
      setSendingOtp(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!captchaVerified) return;
    if (!validate()) return;

    setErrorMessage("");
    setSubmitting(true);

    try {
      const payload = {
        fullName: fullName.trim(),
        email: email.trim(),
        mobile: mobile.trim(),
        message: message.trim(),
        reason,
      };
      if (otpRequired) {
        payload.otp = otp.trim();
      }

      const res = await apiPost("catalog", "/public/feedback", payload);

      if (res?.status === "success") {
        // Fire ad conversions only on a confirmed successful submission.
        // Guarded so a missing/blocked tracking script can never break the
        // success flow below.
        gtag_report_conversion();
        if (typeof window.lintrk === "function") {
          window.lintrk("track", { conversion_id: 28796234 });
        }

        setFormSent(true);
        setFullName("");
        setEmail("");
        setMobile("");
        setMessage("");
        setReason(REASON_OPTIONS[0]);
        setOtp("");
        setOtpSent(false);
        setOtpInfo("");
        setErrors({});
        setCaptchaVerified(false);

        setTimeout(() => {
          setFormSent(false);
        }, 5000);
      } else {
        setErrorMessage(
          res?.message || "Something went wrong. Please try again.",
        );
      }
    } catch (err) {
      console.error("Feedback submit error:", err);
      setErrorMessage(
        err?.response?.data?.message ||
          "Something went wrong. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleCaptchaChange = (value) => {
    // value is the reCAPTCHA token, or null if it expires
    setCaptchaVerified(!!value);
  };

  return (
    <>
      <div className={styles["contact-page"]}>
        {/* 1. HERO SECTION */}
        <section className={styles["contact-hero-dark"]}>
          <div className={styles["contact-hero-mesh"]} />
          <div className={styles["contact-hero-glow"]} />

          <div className="sec-container">
            <div className={styles["contact-hero-row"]}>
              <motion.div
                className={styles["contact-hero-inner"]}
                variants={parentVariants}
                initial="hidden"
                animate="show"
              >
                <motion.div
                  className={styles["contact-eyebrow"]}
                  variants={childVariants}
                >
                  <span className="contact-eyebrow-dot" /> Get in Touch
                </motion.div>
                <motion.h1 className={styles["contact-h1"]} variants={childVariants}>
                  Security reviews, pilots, and <br />
                  <span className={styles["contact-text-gradient"]}>
                    enterprise deployment
                  </span>
                </motion.h1>
                <motion.p className={styles["contact-lead"]} variants={childVariants}>
                  Tell us about your BFSI or regulated workload—we reply on
                  Indian business hours and can share DPA, compliance posture,
                  and scoping for VPC / PaaS.
                </motion.p>
                <motion.div
                  className={styles["contact-trust-list"]}
                  variants={childVariants}
                >
                  <span className={styles["contact-trust-pill"]}>
                    ISO 27001 · ISO 9001
                  </span>
                  <span className={styles["contact-trust-pill"]}>SOC 2 & PCI-ready</span>
                  <span className={styles["contact-trust-pill"]}>DPDPA aligned</span>
                </motion.div>
              </motion.div>

              <motion.div
                variants={parentVariants}
                initial="hidden"
                animate="show"
                style={{ flex: 1, width: "100%" }}
              >
                <ContactHeroArt />
              </motion.div>
            </div>
          </div>
        </section>

        {/* 2. MAIN SECTION */}
        <section className={styles["contact-main"]}>
          <div className="sec-container">
            <div className={styles["contact-contact-grid"]}>
              {/* Sidebar Content */}
              <aside>
                <p className={styles["contact-side-lead"]}>
                  You can also request a sample contract & DPA, ask about our
                  API or SaaS roadmap, or book a call. Delivered by{" "}
                  <strong>Neerja Softwares</strong> — the team behind Doql.
                </p>

                <div className={styles["contact-side-note"]}>
                  <div className={styles["contact-side-note-icon"]}>
                    <Icons.Shield />
                  </div>
                  <p className={styles["contact-side-note-text"]}>
                    For regulated buyers: we can align answers to your vendor
                    questionnaire and security review timelines.
                  </p>
                </div>

                <div className={styles["contact-side-card"]}>
                  <div className={styles["contact-side-card-icon"]}>
                    <Icons.Zap />
                  </div>
                  <h2 className={styles["contact-side-card-title"]}>What to send</h2>
                  <p className={styles["contact-side-card-text"]}>
                    Use the form for bulk APIs, pricing, support, or SaaS / PaaS
                    interest. For longer RFPs, feel free to attach a summary in
                    the message field.
                  </p>
                </div>

                <div className={styles["contact-side-list"]}>
                  <div className={styles["contact-side-line"]}>
                    <span className={styles["contact-side-line-ico"]}>
                      <Icons.Mail />
                    </span>
                    <span>
                      We use your email only to respond to this request.
                    </span>
                  </div>
                  <div className={styles["contact-side-line"]}>
                    <span className={styles["contact-side-line-ico"]}>
                      <Icons.Clock />
                    </span>
                    <span>Typical first response: 1–2 business days.</span>
                  </div>
                </div>

                <Link to="/pricing" className={styles["contact-side-link"]}>
                  View pricing <Icons.ArrowRight />
                </Link>
              </aside>

              {/* Form Panel */}
              <div className={styles["contact-form-panel"]}>
                <div className={styles["contact-form-shine"]} />
                <h2 className={styles["contact-form-title"]}>Send a message</h2>
                <p className={styles["contact-form-hint"]}>
                  Fields marked * are required.
                </p>

                <AnimatePresence mode="wait">
                  {formSent ? (
                    <motion.div
                      className={styles["contact-success"]}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                    >
                      <Icons.Check /> Thank you! Your message has been sent.
                    </motion.div>
                  ) : (
                    <motion.form
                      onSubmit={handleSubmit}
                      initial={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      noValidate
                    >
                      {errorMessage && (
                        <div className={styles["contact-error-banner"]} role="alert">
                          {errorMessage}
                        </div>
                      )}

                      <div className={styles["contact-form-row"]}>
                        <div className={styles["contact-field-group"]}>
                          <label className={styles["contact-label"]}>Full Name *</label>
                          <input
                            type="text"
                            className={`${styles["contact-input"]}${
                              errors.fullName ? ` ${styles["contact-input--error"]}` : ""
                            }`}
                            placeholder="Your name"
                            value={fullName}
                            onChange={(e) => {
                              setFullName(e.target.value);
                              setErrors((prev) => ({ ...prev, fullName: "" }));
                            }}
                            required
                          />
                          {errors.fullName && (
                            <span className={styles["contact-field-error"]}>
                              {errors.fullName}
                            </span>
                          )}
                        </div>
                        <div className={styles["contact-field-group"]}>
                          <label className={styles["contact-label"]}>
                            Email Address *
                          </label>
                          <div className={styles["contact-email-row"]}>
                            <input
                              type="email"
                              className={`${styles["contact-input"]}${
                                errors.email ? ` ${styles["contact-input--error"]}` : ""
                              }`}
                              placeholder="name@company.com"
                              value={email}
                              onChange={(e) => {
                                setEmail(e.target.value);
                                setErrors((prev) => ({ ...prev, email: "" }));
                              }}
                              required
                            />
                            {otpRequired && (
                              <button
                                type="button"
                                className={styles["contact-btn-verify"]}
                                onClick={handleVerifyEmail}
                                disabled={sendingOtp}
                              >
                                {sendingOtp
                                  ? "Sending..."
                                  : otpSent
                                    ? "Resend Code"
                                    : "Verify Email"}
                              </button>
                            )}
                          </div>
                          {errors.email && (
                            <span className={styles["contact-field-error"]}>
                              {errors.email}
                            </span>
                          )}
                          {otpRequired && otpInfo && (
                            <span className={styles["contact-field-success"]}>
                              {otpInfo}
                            </span>
                          )}
                        </div>
                      </div>

                      {otpRequired && otpSent && (
                        <div className={styles["contact-form-row"]}>
                          <div className={styles["contact-field-group"]}>
                            <label className={styles["contact-label"]}>
                              Verification Code *
                            </label>
                            <div className={styles["contact-otp-wrap"]}>
                              <OTPInput
                                numInputs={6}
                                onChange={(value) => {
                                  setOtp(value);
                                  setErrors((prev) => ({ ...prev, otp: "" }));
                                }}
                              />
                            </div>
                            {errors.otp && (
                              <span className={styles["contact-field-error"]}>
                                {errors.otp}
                              </span>
                            )}
                          </div>
                        </div>
                      )}

                      <div className={`${styles["contact-form-row"]} ${styles["contact-form-row--split"]}`}>
                        <div className={styles["contact-field-group"]}>
                          <label className={styles["contact-label"]}>
                            Mobile Number *
                          </label>
                          <input
                            type="tel"
                            inputMode="numeric"
                            className={`${styles["contact-input"]}${
                              errors.mobile ? ` ${styles["contact-input--error"]}` : ""
                            }`}
                            placeholder="10-digit mobile number"
                            value={mobile}
                            onChange={(e) => {
                              setMobile(e.target.value);
                              setErrors((prev) => ({ ...prev, mobile: "" }));
                            }}
                            required
                          />
                          {errors.mobile && (
                            <span className={styles["contact-field-error"]}>
                              {errors.mobile}
                            </span>
                          )}
                        </div>
                        <div className={styles["contact-field-group"]}>
                          <label className={styles["contact-label"]}>
                            Topic of Interest
                          </label>
                          <select
                            className={`${styles["contact-select"]}${
                              errors.reason ? ` ${styles["contact-select--error"]}` : ""
                            }`}
                            value={reason}
                            onChange={(e) => {
                              setReason(e.target.value);
                              setErrors((prev) => ({ ...prev, reason: "" }));
                            }}
                          >
                            {REASON_OPTIONS.map((option) => (
                              <option key={option} value={option}>
                                {option}
                              </option>
                            ))}
                          </select>
                          {errors.reason && (
                            <span className={styles["contact-field-error"]}>
                              {errors.reason}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className={styles["contact-form-row"]}>
                        <div className={styles["contact-field-group"]}>
                          <label className={styles["contact-label"]}>Message *</label>
                          <textarea
                            className={`${styles["contact-textarea"]}${
                              errors.message ? ` ${styles["contact-textarea--error"]}` : ""
                            }`}
                            placeholder="Tell us about your requirements..."
                            value={message}
                            onChange={(e) => {
                              setMessage(e.target.value);
                              setErrors((prev) => ({ ...prev, message: "" }));
                            }}
                            required
                          ></textarea>
                          {errors.message && (
                            <span className={styles["contact-field-error"]}>
                              {errors.message}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* --- Google reCAPTCHA Block --- */}
                      <div className={styles["contact-recaptcha-wrapper"]}>
                        <ReCAPTCHA
                          sitekey={
                            process.env.REACT_APP_RECAPTCHA_SITE_KEY ||
                            "dummy_key"
                          }
                          onChange={handleCaptchaChange}
                        />
                      </div>

                      <button
                        type="submit"
                        className={styles["contact-btn-submit"]}
                        disabled={!captchaVerified || submitting}
                      >
                        {submitting ? "Sending..." : "Send message"}{" "}
                        <Icons.Send />
                      </button>
                    </motion.form>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default ContactUsPage;
